import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { selectAccountCountryCode } from '@app/reducers/account/account.selector';
import { AppState } from '@app/reducers/interfaces';
import { Store } from '@ngrx/store';
import { _, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { SbDateInputComponent } from '@sb/ui/date-picker';
import { SbDialogModule } from '@sb/ui/dialog';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { InputDirective } from '@sb/ui/input';
import { NoticeComponent, NoticeHeaderDirective } from '@sb/ui/notice';
import { PhoneInputComponent } from '@sb/ui/phone-input';
import { SelectComponent, SelectItem } from '@sb/ui/select';
import { plainDateToday } from '@shiftbase-com/ts-shared/utilities';

import { DEFAULT_HIRE_REGION_CODE, SUPPORTED_HIRE_COUNTRIES } from '../../../constants/new-hires.const';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'sb-hire-form',
  standalone: true,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    SbDialogModule,
    SbFormFieldComponent,
    InputDirective,
    SbDateInputComponent,
    NoticeComponent,
    NoticeHeaderDirective,
    PhoneInputComponent,
    SelectComponent,
    CheckboxComponent,
  ],
  styleUrl: './hire-form.component.scss',
  templateUrl: './hire-form.component.html',
})
export class HireFormComponent {
  #translate = inject(TranslateService);
  #featureService = inject(FeatureService);
  #store = inject<Store<AppState>>(Store);

  germanReleaseEnabled = this.#featureService.isEnabled(featureFlag.TMP_GERMAN_RELEASE);

  #accountCountryCode = this.#store.selectSignal(selectAccountCountryCode);

  phoneFallbackRegionCode = computed(() => this.#accountCountryCode() || DEFAULT_HIRE_REGION_CODE);

  form = input.required<FormGroup>();
  hasContractTemplates = input<boolean>(true);
  showGuardianSignature = input<boolean>(false);
  showGuardianCount = input<boolean>(false);
  showGermanFields = input<boolean>(false);
  showPayrollNotice = input<boolean>(false);
  teams = input<SelectItem[]>([]);
  languages = input<{ name: string; value: string }[]>([]);
  employmentCategoryItems = input<SelectItem[]>([]);
  submitted = output<void>();

  languageItems = computed(() =>
    this.languages().map((lang) => ({
      text: lang.name,
      value: lang.value,
    })),
  );

  countryItems: SelectItem[] = SUPPORTED_HIRE_COUNTRIES.map(({ code, name }) => ({
    value: code,
    text: this.#translate.instant(name),
  }));

  // Shown only after "Require guardian signature" is toggled on, so the count starts at 1.
  guardianSignatureCountItems: SelectItem<number>[] = [
    { value: 1, text: this.#translate.instant('1 guardian') },
    { value: 2, text: this.#translate.instant('2 guardians') },
  ];

  foodHandlingItems: SelectItem[] = [
    { value: 'true', text: this.#translate.instant(_('Yes')) },
    { value: 'false', text: this.#translate.instant(_('No')) },
  ];

  today = plainDateToday();
}
